class SamoyedoTierPrice extends HTMLElement {
  connectedCallback() {
    if (this._samoyedoInitialized) {
      this.scheduleRefresh();
      return;
    }

    this._samoyedoInitialized = true;

    this.handleDocumentChange = this.handleDocumentChange.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleCartRefresh = this.handleCartRefresh.bind(this);
    this.handleTierJump = this.handleTierJump.bind(this);

    this.addEventListener('click', this.handleTierJump);

    this.refreshReferences();

    document.addEventListener('input', this.handleDocumentChange, true);
    document.addEventListener('change', this.handleDocumentChange, true);
    document.addEventListener('click', this.handleDocumentChange, true);

    document.addEventListener('variant:change', this.handleVariantChange);
    document.addEventListener('product:variant-change', this.handleVariantChange);
    document.addEventListener('shopify:variant-change', this.handleVariantChange);
    document.addEventListener('samoyedo:cart-refreshed', this.handleCartRefresh);

    window.addEventListener('popstate', this.handleVariantChange);

    this.scheduleRefresh();
  }

  disconnectedCallback() {
    document.removeEventListener('input', this.handleDocumentChange, true);
    document.removeEventListener('change', this.handleDocumentChange, true);
    document.removeEventListener('click', this.handleDocumentChange, true);

    document.removeEventListener('variant:change', this.handleVariantChange);
    document.removeEventListener('product:variant-change', this.handleVariantChange);
    document.removeEventListener('shopify:variant-change', this.handleVariantChange);
    document.removeEventListener('samoyedo:cart-refreshed', this.handleCartRefresh);

    window.removeEventListener('popstate', this.handleVariantChange);

    this.removeEventListener('click', this.handleTierJump);

    for (const timeout of this._refreshTimeouts || []) {
      window.clearTimeout(timeout);
    }
  }

  scheduleRefresh() {
    for (const timeout of this._refreshTimeouts || []) {
      window.clearTimeout(timeout);
    }

    this._refreshTimeouts = [0, 60, 150, 300, 600].map((delay) =>
      window.setTimeout(() => {
        this.refreshReferences();
        this.update(true);
      }, delay)
    );
  }

  handleCartRefresh() {
    this.scheduleRefresh();
  }

  handleVariantChange() {
    this.scheduleRefresh();
  }

  handleDocumentChange(event) {
    const target = event.target;

    if (!target) return;

    const isQuantityChange =
      target.matches?.(
        'input[type="number"], input[name="quantity"], input[name="updates[]"]'
      ) ||
      target.closest?.(
        'quantity-selector-component, cart-quantity-selector-component'
      );

    const isVariantIdChange =
      target.matches?.('input[name="id"], select[name="id"]');

    const isVariantOptionChange =
      target.matches?.(
        'input[type="radio"], select, [data-option-value-id], [data-variant-id], [data-variant-option], [data-option-value], [data-product-option]'
      ) ||
      target.closest?.(
        'variant-picker, variant-selects, variant-radios, product-variant-picker, [data-option-value-id], [data-variant-id], [data-variant-option], [data-option-value], [data-product-option], .variant-option, .variant-picker, .product-form__input'
      );

    if (isQuantityChange || isVariantIdChange || isVariantOptionChange) {
      this.scheduleRefresh();
    }
  }

  handleTierJump(event) {
    const button = event.target.closest?.('[data-tier-jump]');
    if (!button) return;

    event.preventDefault();

    const normalize = (value) =>
      String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();

    const matchesDiscountTitle = (element) => {
      const text = normalize(element?.textContent);
      return text === 'descuento por cantidad' || text.startsWith('descuento por cantidad ');
    };

    let target = [...document.querySelectorAll('summary, button, [role="button"]')].find(matchesDiscountTitle);

    if (!target) {
      const textElements = [...document.querySelectorAll('h1, h2, h3, h4, h5, h6, p, span, strong, div')]
        .filter(matchesDiscountTitle);

      target = textElements.sort((first, second) => first.children.length - second.children.length)[0];
    }

    if (!target) {
      console.warn('No se encontró el bloque "Descuento por cantidad".');
      return;
    }

    const details = target.closest('details');

    if (details) {
      details.open = true;
    } else if (target.matches('summary, button, [role="button"]')) {
      const expanded = target.getAttribute('aria-expanded');

      if (expanded === 'false') {
        target.click();
      }
    }

    window.setTimeout(() => {
      const scrollTarget = details || target;
      const headerOffset = 110;

      const top = scrollTarget.getBoundingClientRect().top + window.scrollY - headerOffset;

      window.scrollTo({
        top: Math.max(0, top),
        behavior: 'smooth',
      });
    }, 80);
  }

  refreshReferences() {
    this.priceEl = this.querySelector('[data-tier-price]');
    this.basePriceEl = this.querySelector('[data-tier-base-price]');
    this.labelEl = this.querySelector('[data-tier-label]');
    this.savingEl = this.querySelector('[data-tier-saving]');
    this.savingTextEl = this.querySelector('[data-tier-saving-text]');
    this.totalEl = this.querySelector('[data-tier-total]');
    this.totalLabelEl = this.querySelector('[data-tier-total-label]');
    this.selectionEl = this.querySelector('[data-tier-selection]');
    this.breakdownEl = this.querySelector('[data-tier-breakdown]');
    this.tierCardEls = [...this.querySelectorAll('[data-tier-card]')];
    this.tierCardPriceEls = [...this.querySelectorAll('[data-tier-card-price]')];
    this.nextIncentiveEl = this.querySelector('[data-tier-next-incentive]');
    this.nextIncentiveTextEl = this.querySelector('[data-tier-next-incentive-text]');

    const json = this.querySelector('[data-samoyedo-variants]');
    const jsonText = json?.textContent?.trim() || '[]';

    if (jsonText === this._lastJsonText) {
      return;
    }

    try {
      const parsed = JSON.parse(jsonText);
      this.variants = Array.isArray(parsed) ? parsed : [];
      this._lastJsonText = jsonText;
    } catch (error) {
      console.error('Precio por cantidad: JSON de variantes inválido', error);
      this.variants = [];
    }
  }

  isCartContext() {
    return this.dataset.context === 'cart';
  }

  isServiceContext() {
    const text = String(this.textContent || '').toLowerCase();

    return (
      this.classList.contains('samoyedo-tier-price--service') ||
      this.querySelector('.samoyedo-tier-price__box--service') ||
      text.includes('precio personalización') ||
      text.includes('servicio seleccionado') ||
      text.includes('dto. dtf') ||
      text.includes('personalizacion dtf') ||
      text.includes('personalización dtf')
    );
  }

  isDtfContext() {
    const text = String(this.textContent || '').toLowerCase();

    return (
      text.includes('dtf') ||
      [...this.tierCardEls || []].some((card) =>
        String(card.dataset.tierCard || '').startsWith('dtf-tier-')
      )
    );
  }

  getProductScope() {
    if (this.isCartContext()) {
      return (
        this.closest('.cart-item, cart-drawer-component, cart-items-component') ||
        this
      );
    }

    return (
      this.closest('product-info') ||
      this.closest('product-form-component') ||
      this.closest('.product-information') ||
      this.closest('[data-product-id]') ||
      this.closest('.shopify-section') ||
      document
    );
  }

  getVariantIdFromUrl() {
    try {
      const params = new URLSearchParams(window.location.search);
      return Number(params.get('variant') || 0);
    } catch {
      return 0;
    }
  }

  getVariantId() {
    if (this.isCartContext()) {
      return Number(this.dataset.variantId || 0);
    }

    const scope = this.getProductScope();

    const productForm =
      scope.querySelector('form[action*="/cart/add"]') ||
      this.closest('form[action*="/cart/add"]');

    const variantInput =
      productForm?.querySelector('input[name="id"], select[name="id"]') ||
      scope.querySelector('input[name="id"], select[name="id"]');

    const inputVariantId = Number(variantInput?.value || 0);

    if (inputVariantId > 0 && this.variantExists(inputVariantId)) {
      return inputVariantId;
    }

    const urlVariantId = this.getVariantIdFromUrl();

    if (urlVariantId > 0 && this.variantExists(urlVariantId)) {
      return urlVariantId;
    }

    const datasetVariantId = Number(this.dataset.variantId || 0);

    if (datasetVariantId > 0 && this.variantExists(datasetVariantId)) {
      return datasetVariantId;
    }

    return 0;
  }

  variantExists(variantId) {
    return Boolean(
      this.variants?.some((variant) => Number(variant.id) === Number(variantId))
    );
  }

  getQuantity() {
    const scope = this.getProductScope();

    const wrapper =
      this.closest('.quantity-selector-wrapper') ||
      scope.querySelector('.quantity-selector-wrapper');

    const input =
      wrapper?.querySelector(
        'cart-quantity-selector-component input[type="number"], quantity-selector-component input[type="number"], input[name="updates[]"], input[name="quantity"]'
      ) ||
      scope.querySelector('input[name="quantity"]');

    const value = Number(input?.value);

    if (Number.isFinite(value) && value > 0) {
      return Math.floor(value);
    }

    return Math.max(1, Number(this.dataset.initialQuantity || 1));
  }

  getTier(quantity) {
    if (quantity >= 50) {
      return {
        key: 'tier-50',
        label: '50+ unidades · 23% dto.',
        factor: 0.77,
        discount: 23,
      };
    }

    if (quantity >= 25) {
      return {
        key: 'tier-25',
        label: '25–49 unidades · 18% dto.',
        factor: 0.82,
        discount: 18,
      };
    }

    if (quantity >= 10) {
      return {
        key: 'tier-10',
        label: '10–24 unidades · 12% dto.',
        factor: 0.88,
        discount: 12,
      };
    }

    if (quantity >= 5) {
      return {
        key: 'tier-5',
        label: '5–9 unidades · 7% dto.',
        factor: 0.93,
        discount: 7,
      };
    }

    return {
      key: 'tier-1',
      label: '1–4 unidades · 0% dto.',
      factor: 1,
      discount: 0,
    };
  }

  getDtfTier(quantity) {
    if (quantity >= 50) {
      return {
        key: 'dtf-tier-50',
        label: '50+ unidades · 12% dto. DTF',
        factor: 0.88,
        discount: 12,
      };
    }

    if (quantity >= 30) {
      return {
        key: 'dtf-tier-30',
        label: '30–49 unidades · 8% dto. DTF',
        factor: 0.92,
        discount: 8,
      };
    }

    if (quantity >= 20) {
      return {
        key: 'dtf-tier-20',
        label: '20–29 unidades · 5% dto. DTF',
        factor: 0.95,
        discount: 5,
      };
    }

    return {
      key: 'dtf-tier-1',
      label: '1–19 unidades · 0% dto. DTF',
      factor: 1,
      discount: 0,
    };
  }

  getTierPrices(originalPrice) {
    return {
      'tier-1': this.roundCents(originalPrice),
      'tier-5': this.roundCents(originalPrice * 0.93),
      'tier-10': this.roundCents(originalPrice * 0.88),
      'tier-25': this.roundCents(originalPrice * 0.82),
      'tier-50': this.roundCents(originalPrice * 0.77),
    };
  }

  getDtfTierPrices(originalPrice) {
    return {
      'dtf-tier-1': this.roundCents(originalPrice),
      'dtf-tier-20': this.roundCents(originalPrice * 0.95),
      'dtf-tier-30': this.roundCents(originalPrice * 0.92),
      'dtf-tier-50': this.roundCents(originalPrice * 0.88),
    };
  }

  getNextTierIncentive(quantity, originalPrice) {
    const tierPrices = this.getTierPrices(originalPrice);

    if (quantity < 5) {
      return {
        needed: 5 - quantity,
        price: tierPrices['tier-5'],
      };
    }

    if (quantity < 10) {
      return {
        needed: 10 - quantity,
        price: tierPrices['tier-10'],
      };
    }

    if (quantity < 25) {
      return {
        needed: 25 - quantity,
        price: tierPrices['tier-25'],
      };
    }

    if (quantity < 50) {
      return {
        needed: 50 - quantity,
        price: tierPrices['tier-50'],
      };
    }

    return null;
  }

  roundCents(value) {
    const number = Number(value);

    if (!Number.isFinite(number)) {
      return 0;
    }

    return Math.round(number + Number.EPSILON);
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  }

  formatQuantityLabel(quantity) {
    const safeQuantity = Math.max(1, Number(quantity) || 1);

    return safeQuantity === 1
      ? 'Total (1 unidad)'
      : `Total (${safeQuantity} unidades)`;
  }

  getVariantOption(variant, optionName, fallbackIndex) {
    const normalizedName = String(optionName).trim().toLowerCase();

    if (Array.isArray(variant.selected_options)) {
      const selected = variant.selected_options.find(
        (option) => String(option.name).trim().toLowerCase() === normalizedName
      );

      if (selected?.value) {
        return selected.value;
      }
    }

    if (Array.isArray(variant.selectedOptions)) {
      const selected = variant.selectedOptions.find(
        (option) => String(option.name).trim().toLowerCase() === normalizedName
      );

      if (selected?.value) {
        return selected.value;
      }
    }

    return variant[`option${fallbackIndex}`] || '';
  }

  updateSelectionText(variant) {
    if (!this.selectionEl) {
      return;
    }

    if (this.isServiceContext()) {
      const title = variant.title || variant.option1 || '';
      this.selectionEl.textContent = title
        ? `Servicio seleccionado: ${title}`
        : 'Servicio seleccionado';
      return;
    }

    const size = this.getVariantOption(variant, 'talla', 1);
    const color = this.getVariantOption(variant, 'color', 2);

    const parts = [];

    if (size) {
      parts.push(`Talla seleccionada: ${size}`);
    }

    if (color) {
      parts.push(`Color: ${color}`);
    }

    this.selectionEl.textContent = parts.join(' · ');
  }

  updateBasePrice(originalPrice, hasDiscount) {
    if (!this.basePriceEl) {
      return;
    }

    this.basePriceEl.textContent = this.formatMoney(originalPrice);

    this.basePriceEl.classList.toggle(
      'samoyedo-tier-price__base--hidden',
      !hasDiscount
    );
  }

  updateSaving(originalPrice, unitPrice, hasDiscount) {
    if (!this.savingEl) {
      return;
    }

    this.savingEl.classList.toggle(
      'samoyedo-tier-price__saving--hidden',
      !hasDiscount
    );

    if (!hasDiscount || !this.savingTextEl) {
      return;
    }

    const saving = Math.max(0, this.roundCents(originalPrice - unitPrice));

    this.savingTextEl.textContent =
      `Ahorras ${this.formatMoney(saving)} por unidad`;
  }

  updateDiscountColor(hasDiscount) {
    if (!this.priceEl) {
      return;
    }

    this.priceEl.classList.toggle(
      'samoyedo-tier-price__value--discounted',
      hasDiscount
    );
  }

  updateTotalLabel(quantity) {
    if (!this.totalLabelEl) {
      return;
    }

    this.totalLabelEl.textContent = this.formatQuantityLabel(quantity);
  }

  updateTierBreakdown(originalPrice, activeTierKey) {
    const isDtf = this.isServiceContext() && this.isDtfContext();

    const tierPrices = isDtf
      ? this.getDtfTierPrices(originalPrice)
      : this.getTierPrices(originalPrice);

    for (const priceEl of this.tierCardPriceEls || []) {
      const key = priceEl.dataset.tierCardPrice;

      if (key && Object.prototype.hasOwnProperty.call(tierPrices, key)) {
        priceEl.textContent = this.formatMoney(tierPrices[key]);
      }
    }

    for (const cardEl of this.tierCardEls || []) {
      const key = cardEl.dataset.tierCard;

      cardEl.classList.toggle(
        'samoyedo-tier-price__tier-card--active',
        key === activeTierKey
      );
    }

    if (this.breakdownEl) {
      this.breakdownEl.dataset.activeTier = activeTierKey;
    }
  }

  updateNextIncentive(quantity, originalPrice) {
    if (!this.nextIncentiveEl || !this.nextIncentiveTextEl) {
      return;
    }

    if (this.isServiceContext()) {
      this.nextIncentiveEl.classList.add('samoyedo-tier-price__next--hidden');
      return;
    }

    const incentive = this.getNextTierIncentive(quantity, originalPrice);

    if (!incentive) {
      this.nextIncentiveEl.classList.add('samoyedo-tier-price__next--hidden');
      return;
    }

    this.nextIncentiveEl.classList.remove('samoyedo-tier-price__next--hidden');

    this.nextIncentiveTextEl.textContent =
      `Añade ${incentive.needed} más y baja a ${this.formatMoney(incentive.price)}`;
  }

  update(force = false) {
    if (
      !this.priceEl ||
      !this.labelEl ||
      !Array.isArray(this.variants) ||
      this.variants.length === 0
    ) {
      this.refreshReferences();
    }

    const variantId = this.getVariantId();
    const quantity = this.getQuantity();

    const variant = this.variants?.find(
      (item) => Number(item.id) === Number(variantId)
    );

    if (!variant || !this.priceEl || !this.labelEl) {
      return;
    }

    const state = `${variant.id}:${quantity}:${this.isServiceContext()}:${this.isDtfContext()}`;

    if (!force && state === this.lastState) {
      return;
    }

    this.lastState = state;

    const originalPrice = Number(variant.price);

    if (!Number.isFinite(originalPrice)) {
      return;
    }

    const isService = this.isServiceContext();
    const isDtf = isService && this.isDtfContext();

    const tier = isDtf
      ? this.getDtfTier(quantity)
      : isService
        ? {
            key: 'service-no-discount',
            label: 'Sin descuento de prendas',
            factor: 1,
            discount: 0,
          }
        : this.getTier(quantity);

    const unitPrice = this.roundCents(originalPrice * tier.factor);
    const lineTotal = this.roundCents(unitPrice * quantity);
    const hasDiscount = tier.discount > 0;

    this.priceEl.textContent = this.formatMoney(unitPrice);
    this.labelEl.textContent = tier.label;

    this.updateDiscountColor(hasDiscount);
    this.updateBasePrice(originalPrice, hasDiscount);
    this.updateSaving(originalPrice, unitPrice, hasDiscount);

    if (this.totalEl) {
      this.totalEl.textContent = this.formatMoney(lineTotal);
    }

    this.updateTotalLabel(quantity);
    this.updateTierBreakdown(originalPrice, tier.key);
    this.updateNextIncentive(quantity, originalPrice);
    this.updateSelectionText(variant);

    this.dataset.variantId = String(variant.id);
  }
}

if (!customElements.get('samoyedo-tier-price')) {
  customElements.define('samoyedo-tier-price', SamoyedoTierPrice);
}